"use client";

import { Button } from "@borderless/ui/components/button";
import { Card } from "@borderless/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@borderless/ui/components/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@borderless/ui/components/tabs";
import { useIsMobile } from "@borderless/ui/hooks/use-mobile";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "@borderless/ui/lib/recharts";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";

export type FieldsOfStudyProps = {
  majors: {
    id: string;
    name: string;
    completions: number;
    percentage: number;
  }[];
  dataCycle: string;
};

// Indigo color for charts
const INDIGO_COLOR = "#6366f1";

const formatPercentage = (percentage: number) => {
  return percentage < 0.1 ? "<0.1%" : `${percentage.toFixed(1)}%`;
};

// Custom tooltip component for the charts
const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: {
    payload: {
      name: string;
      completions: number;
      percentage: number;
    };
  }[];
}) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;

    if (!data) return null;

    return (
      <div className="rounded-md border border-border bg-card p-2 shadow-md">
        <p className="font-medium text-card-foreground">{data.name}</p>
        <p className="text-card-foreground">
          {data.completions.toLocaleString()} students,{" "}
          {formatPercentage(data.percentage)}
        </p>
      </div>
    );
  }
  return null;
};

// Custom label for the pie chart
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  completions,
  showPercentage,
}: {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  completions: string;
  showPercentage: boolean;
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight="bold"
    >
      {showPercentage ? `${(percent * 100).toFixed(1)}%` : completions}
    </text>
  );
};

export function FieldsOfStudy(props: FieldsOfStudyProps) {
  // Calculate total completions
  const totalCompletions = props.majors.reduce(
    (sum, major) => sum + major.completions,
    0,
  );

  // Sort data by completions (descending)
  const sortedData = [...props.majors].sort(
    (a, b) => b.completions - a.completions,
  );

  const [showPercentage, setShowPercentage] = useState(true);
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("chart");
  const [showAllMajors, setShowAllMajors] = useState(false);

  // Set default tab based on screen size
  useEffect(() => {
    setActiveTab(isMobile ? "donut" : "chart");
  }, [isMobile]);

  // Get display data based on showAllMajors state
  const displayData = showAllMajors ? sortedData : sortedData.slice(0, 10);

  // Prepare data for donut chart (top 10 + Others)
  const donutChartData = sortedData.slice(0, 10);
  if (sortedData.length > 10) {
    const othersCompletions = sortedData
      .slice(10)
      .reduce((sum, major) => sum + major.completions, 0);
    const othersPercentage = sortedData
      .slice(10)
      .reduce((sum, major) => sum + major.percentage, 0);

    donutChartData.push({
      id: "others",
      name: "Others",
      completions: othersCompletions,
      percentage: othersPercentage,
    });
  }

  const ExpandFooter = () => {
    return (
      <div className="flex flex-col items-center justify-center gap-2">
        {sortedData.length > 10 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAllMajors(!showAllMajors)}
          >
            {showAllMajors ? (
              <>
                <ChevronUp className="mr-1 h-4 w-4" />
                Show Top 10
              </>
            ) : (
              <>
                <ChevronDown className="mr-1 h-4 w-4" />
                Show All Majors
              </>
            )}
          </Button>
        )}
        <span className="self-start text-xs text-subtle">
          Students graduated in {props.dataCycle}
        </span>
      </div>
    );
  };

  return (
    <Card>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="chart">Bar Chart</TabsTrigger>
          <TabsTrigger value="table">Table View</TabsTrigger>
          <TabsTrigger value="donut">Donut Chart</TabsTrigger>
        </TabsList>

        <TabsContent value="chart" className="pt-4">
          <div className="overflow-x-auto">
            <div
              className="w-full min-w-[800px]"
              style={{
                height: `${Math.max(500, displayData.length * 50)}px`,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={displayData}
                  layout="vertical"
                  margin={{ top: 20, right: 50, left: 200, bottom: 20 }}
                >
                  <XAxis
                    type="number"
                    domain={
                      showPercentage
                        ? [
                            0,
                            Math.ceil(
                              Math.max(
                                ...displayData.map((d) => d.percentage),
                              ) / 5,
                            ) * 5,
                          ]
                        : [
                            0,
                            Math.ceil(
                              Math.max(
                                ...displayData.map((d) => d.completions),
                              ) / 100,
                            ) * 100,
                          ]
                    }
                    hide
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={30}
                    tickLine={false}
                    axisLine={false}
                    tick={(props: {
                      x: number;
                      y: number;
                      payload: { value: string };
                    }) => {
                      const { x, y, payload } = props;
                      // Split text into chunks of ~25 characters, trying to break at spaces
                      const text = payload.value;
                      const words = text.split(/(?<=\s)/);
                      const lines = [];
                      let currentLine = "";

                      words.forEach((word: string) => {
                        if ((currentLine + word).length > 25) {
                          lines.push(currentLine);
                          currentLine = word;
                        } else {
                          currentLine += word;
                        }
                      });
                      if (currentLine) {
                        lines.push(currentLine);
                      }

                      return (
                        <g transform={`translate(${x},${y})`}>
                          {lines.map((line, index) => (
                            <text
                              key={index}
                              x={0}
                              y={index * 14 - (lines.length - 1) * 7}
                              textAnchor="end"
                              fill="var(--subtle)"
                              fontSize={12}
                            >
                              {line.trim()}
                            </text>
                          ))}
                        </g>
                      );
                    }}
                  />
                  <Bar
                    dataKey={showPercentage ? "percentage" : "completions"}
                    radius={4}
                    barSize={30}
                    fill={INDIGO_COLOR}
                  >
                    <LabelList
                      dataKey={showPercentage ? "percentage" : "completions"}
                      position="right"
                      formatter={(value: number) =>
                        showPercentage
                          ? formatPercentage(value)
                          : value.toLocaleString()
                      }
                      fill="var(--subtle)"
                      style={{ fontWeight: "medium" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <ExpandFooter />
        </TabsContent>

        <TabsContent value="table" className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-muted-foreground">Major</TableHead>
                <TableHead className="text-right text-muted-foreground">
                  %
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayData.map((major) => (
                <TableRow key={major.id}>
                  <TableCell className="font-medium">{major.name}</TableCell>
                  <TableCell className="text-right">
                    {showPercentage
                      ? formatPercentage(major.percentage)
                      : major.completions.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ExpandFooter />
        </TabsContent>

        <TabsContent value="donut" className="pt-4">
          <div className="h-[500px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={130}
                  dataKey="percentage"
                  nameKey="name"
                  labelLine={false}
                  label={(props: {
                    cx: number;
                    cy: number;
                    midAngle: number;
                    innerRadius: number;
                    outerRadius: number;
                    percent: number;
                    completions: string;
                  }) =>
                    renderCustomizedLabel({
                      ...props,
                      showPercentage,
                    })
                  }
                >
                  {donutChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={INDIGO_COLOR}
                      opacity={entry.name === "Others" ? 0.3 : 1 - index * 0.07}
                      stroke="var(--background)"
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {(showAllMajors ? displayData : donutChartData).map(
                (major, index) => (
                  <div key={major.name} className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{
                        backgroundColor: "var(--primary)",
                        opacity:
                          major.name === "Others" ? 0.3 : 1 - index * 0.1,
                      }}
                    />
                    <span className="text-sm">
                      {major.name} (
                      {showPercentage
                        ? formatPercentage(major.percentage)
                        : major.completions.toLocaleString()}
                      )
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
          <ExpandFooter />
        </TabsContent>
      </Tabs>
    </Card>
  );
}
